import { useFlag, useFlagsStatus } from '@unleash/proxy-client-react';

import type { FeatureFlagsStatus } from './feature-flags.types';

import { FeatureFlags } from './feature-flags.constants';

/**
 * The canonical hook for consuming feature flags. Returns a typed object with named
 * boolean properties for each registered flag, derived from `FeatureFlags`.
 *
 * Adding a new flag: add one entry to `FeatureFlags` in `feature-flags.constants.ts`.
 * It will automatically appear as a property on the returned object.
 *
 * @returns an object with a boolean property per registered feature flag
 *
 * @example
 * ```tsx
 * const { isOtpwFlowFeatureEnabled } = useFeatureFlags();
 * ```
 */
export function useFeatureFlags(): Record<keyof typeof FeatureFlags, boolean> {
    // FeatureFlags is a compile-time constant (`as const`); the number and order of entries
    // never change at runtime, so the hook call count is stable across renders.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return Object.fromEntries(
        Object.entries(FeatureFlags).map(([key, flagName]) => [key, useFlag(flagName)])
    ) as Record<keyof typeof FeatureFlags, boolean>;
}

/**
 * Where the feature flags stand: resolved once Unleash answered the first fetch, with toggles or with
 * an error, so a flag that still reads off is off for real.
 *
 * @returns the status of the feature flags
 *
 * @example
 * ```tsx
 * const { isResolved } = useFeatureFlagsStatus();
 * ```
 */
export function useFeatureFlagsStatus(): FeatureFlagsStatus {
    const { flagsError, flagsReady } = useFlagsStatus();

    return { isResolved: flagsReady || Boolean(flagsError) };
}
